import { useState, useEffect, Fragment } from "react";
import {
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Box,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Invoice, resetForm, setDialog } from "../../../../app/slices/invoiceSlice";
import { SelectSupplier } from "../../../../components/Selectors/SelectSupplier";
import { SelectProducts } from "../../../../components/Selectors/SelectProducts";
import { NumericFormat } from "react-number-format";
import { BiTrash, BiEdit } from "react-icons/bi";
import { IoChevronDown } from "react-icons/io5";
import { invoiceTotals, num } from "../../../../helpers/invoiceTotals";
import SerialNumbersInput from "../../../../components/SerialNumbersInput";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineCube,
  HiOutlineDocumentText,
} from "react-icons/hi2";
import FormDialog, {
  PlainCard,
  SectionCard,
} from "../../../../components/FormDialog";

const emptyDraft = {
  product_id: null,
  product_name: "",
  is_serialized: false,
  quantity: 1,
  price: 0,
  discount: 0,
  serial_numbers: [],
};

const InvoiceForm = () => {
  const dispatch = useDispatch();

  const _Invoice = useSelector((state) => state.invoice.invoice);
  const _SearchBody = useSelector((state) => state.invoice.searchBody);
  const _Supplier = useSelector((state) => state.supplier.suppliersList.data);
  const _Products = useSelector((state) => state.product.products.data);

  const [loading, setLoading] = useState(false);
  // One line at a time is edited here, so the product selector is mounted once
  // instead of once per line; a 100 line invoice used to mean 100 identical
  // product requests.
  const [draft, setDraft] = useState(emptyDraft);
  const [editIndex, setEditIndex] = useState(null);
  const [draftError, setDraftError] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  const isAdd = _Invoice.form.id ? false : true;
  const { control, reset, handleSubmit, setValue, watch, getValues } = useForm({
    defaultValues: _Invoice.form,
  });

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "items",
  });

  const items = watch("items") ?? [];

  // Called after any price / discount / quantity change; invoiceTotals owns the
  // arithmetic, this only writes the results back onto the form.
  const recalc = () => {
    const { lines, total_amount } = invoiceTotals(
      getValues("items"),
      getValues("discount")
    );
    lines.forEach((line, index) => {
      setValue(`items[${index}].net_price`, line.net_price);
      setValue(`items[${index}].total_amount`, line.total_amount);
    });
    setValue("total_amount", total_amount);
  };

  // Lines only change through the editor below, so recalculating off the field
  // array covers add, edit and delete in one place.
  useEffect(() => {
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const onSubmit = (data) => {
    setLoading(true);
    const body = {
      supplier_id: data.supplier_id,
      discount: num(data.discount),
      total_amount: num(data.total_amount),
      items: data.items.map((item) => ({
        product_id: item.product_id,
        quantity: num(item.quantity),
        price: num(item.price),
        discount: num(item.discount),
        net_price: num(item.net_price),
        total_amount: num(item.total_amount),
        serial_numbers: item.serial_numbers ?? [],
      })),
    };
    const action = isAdd
      ? Invoice.create(body, _SearchBody)
      : Invoice.update(data.id, body, _SearchBody);
    dispatch(action)
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    reset({ ..._Invoice.form });
    setDraft(emptyDraft);
    setEditIndex(null);
    setDraftError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_Invoice.form]);

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The shared product list shifts as other searches run, so the flag is
  // stamped onto the line when a product is picked. Existing invoices loaded
  // for edit have no flag, hence the lookup and serials fallbacks.
  const isSerialized = (item) => {
    if (item.is_serialized !== undefined && item.is_serialized !== null)
      return item.is_serialized;
    const product = _Products.find((x) => x.id === item.product_id);
    if (product) return product.is_serialized;
    return (item.serial_numbers ?? []).length > 0;
  };

  // A serialized line must carry exactly one serial per unit.
  const serialsIncomplete = items.some(
    (item) =>
      isSerialized(item) &&
      (item.serial_numbers ?? []).length !== num(item.quantity)
  );

  const draftTotals = invoiceTotals([draft]).lines[0];
  const draftSerialized = isSerialized(draft);

  const saveDraft = () => {
    if (!draft.product_id) return setDraftError("اختر المنتج");
    if (num(draft.quantity) <= 0)
      return setDraftError("العدد يجب ان يكون اكبر من صفر");
    const duplicate = items.some(
      (item, index) => item.product_id === draft.product_id && index !== editIndex
    );
    if (duplicate) return setDraftError("المنتج مضاف مسبقا في هذه الفاتورة");
    if (
      draftSerialized &&
      (draft.serial_numbers ?? []).length !== num(draft.quantity)
    )
      return setDraftError("عدد الارقام التسلسلية لا يطابق العدد");

    const line = {
      ...draft,
      quantity: num(draft.quantity),
      price: num(draft.price),
      discount: num(draft.discount),
      net_price: draftTotals.net_price,
      total_amount: draftTotals.total_amount,
    };
    if (editIndex === null) append(line);
    else update(editIndex, line);
    setDraft(emptyDraft);
    setEditIndex(null);
    setDraftError("");
  };

  const editLine = (index) => {
    // Saved lines carry no flag, and emptyDraft's false would hide the serials.
    setDraft({
      ...emptyDraft,
      ...items[index],
      is_serialized: isSerialized(items[index]),
    });
    setEditIndex(index);
    setDraftError("");
  };

  const cancelEdit = () => {
    setDraft(emptyDraft);
    setEditIndex(null);
    setDraftError("");
  };

  const productName = (item) =>
    item.product_name ??
    _Products.find((x) => x.id === item.product_id)?.name ??
    `#${item.product_id}`;

  return (
    <FormDialog
      open={_Invoice.dialog}
      // Only the close buttons dismiss it; a stray click on the backdrop used
      // to throw away a half filled invoice.
      onClose={(e, reason) =>
        reason !== "backdropClick" && dispatch(setDialog())
      }
      maxWidth="md"
      icon={<HiOutlineClipboardDocumentList />}
      title={isAdd ? "أضافة فاتورة" : "تعديل فاتورة"}
      subtitle={
        isAdd
          ? "أدخل معلومات الفاتورة والمواد لإضافتها"
          : "قم بتحديث معلومات الفاتورة ثم احفظ التغييرات"
      }
      onSubmit={handleSubmit(onSubmit)}
      saving={loading}
      saveDisabled={items.length === 0 || serialsIncomplete}
      loading={_Invoice.loading}
    >
      <Stack
        spacing={2.5}
        // Enter belongs to the barcode scanner and the product list, not to
        // saving the invoice.
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
      >
        <SectionCard icon={<HiOutlineDocumentText />} title="معلومات الفاتورة">
          <Grid item xs={12}>
            <Controller
              name="supplier_id"
              control={control}
              render={({ field }) => (
                <SelectSupplier
                  required
                  onChange={(e, newValue) => {
                    setValue("supplier_id", newValue ? newValue.id : null);
                  }}
                  value={
                    _Supplier.filter((x) => x.id === field.value)[0] ?? null
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="discount"
              control={control}
              render={({ field }) => (
                <NumericFormat
                  thousandSeparator
                  customInput={TextField}
                  value={field.value}
                  onChange={(e) => {
                    setValue("discount", num(e.target.value));
                    recalc();
                  }}
                  valueIsNumericString
                  allowNegative={false}
                  fullWidth
                  size="small"
                  label="خصم الفاتورة"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">IQD</InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="total_amount"
              control={control}
              render={({ field }) => (
                <NumericFormat
                  thousandSeparator
                  customInput={TextField}
                  value={field.value}
                  disabled
                  fullWidth
                  size="small"
                  label="المبلغ الكلي بعد الخصم"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">IQD</InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </SectionCard>

        <SectionCard
          icon={<HiOutlineCube />}
          title={editIndex === null ? "اضافة مادة" : `تعديل المادة ${editIndex + 1}`}
        >
          <Grid item xs={12}>
            <SelectProducts
              // The selector remembers its own pick and its typed text,
              // so clearing the draft is not enough; a new key gives the
              // next line a fresh one.
              key={editIndex ?? items.length}
              onChange={(e, newValue) =>
                setDraft((prev) => ({
                  ...prev,
                  product_id: newValue ? newValue.id : null,
                  product_name: newValue ? newValue.name : "",
                  price: newValue ? newValue.buying_price : 0,
                  is_serialized: newValue?.is_serialized ?? false,
                  serial_numbers: newValue?.is_serialized
                    ? prev.serial_numbers
                    : [],
                }))
              }
              value={
                draft.product_id
                  ? _Products.find((x) => x.id === draft.product_id) ?? {
                    id: draft.product_id,
                    name: draft.product_name,
                  }
                  : null
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <NumericFormat
              thousandSeparator
              customInput={TextField}
              value={draft.price}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  price: num(e.target.value),
                }))
              }
              valueIsNumericString
              allowNegative={false}
              fullWidth
              size="small"
              label="السعر"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <NumericFormat
              thousandSeparator
              customInput={TextField}
              value={draft.discount}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  discount: num(e.target.value),
                }))
              }
              valueIsNumericString
              allowNegative={false}
              fullWidth
              size="small"
              label="الخصم"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <NumericFormat
              thousandSeparator
              customInput={TextField}
              value={draft.quantity}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  quantity: num(e.target.value),
                }))
              }
              valueIsNumericString
              allowNegative={false}
              fullWidth
              size="small"
              label="العدد"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <NumericFormat
              thousandSeparator
              customInput={TextField}
              value={draftTotals.total_amount}
              disabled
              fullWidth
              size="small"
              label="المبلغ الكلي"
            />
          </Grid>
          {draftSerialized ? (
            <Grid item xs={12}>
              <SerialNumbersInput
                value={draft.serial_numbers}
                quantity={num(draft.quantity)}
                productId={draft.product_id}
                listOnly
                // This invoice's saved serials are its own, not taken.
                exclude={
                  _Invoice.form.items.find(
                    (x) => x.product_id === draft.product_id
                  )?.serial_numbers
                }
                onChange={(newValue) =>
                  setDraft((prev) => ({
                    ...prev,
                    serial_numbers: newValue,
                  }))
                }
              />
            </Grid>
          ) : null}
          {draftError ? (
            <Grid item xs={12}>
              <Alert severity="error">{draftError}</Alert>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={saveDraft}>
                {editIndex === null ? "اضافة المادة" : "حفظ التعديل"}
              </Button>
              {editIndex === null ? null : (
                <Button variant="outlined" onClick={cancelEdit}>
                  الغاء
                </Button>
              )}
            </Stack>
          </Grid>
        </SectionCard>

        <PlainCard sx={{ overflowX: "auto" }}>
          {items.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>#</TableCell>
                  <TableCell>المنتج</TableCell>
                  <TableCell>السعر</TableCell>
                  <TableCell>الخصم</TableCell>
                  <TableCell>السعر الصافي</TableCell>
                  <TableCell>العدد</TableCell>
                  <TableCell>المبلغ الكلي</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const serials = item.serial_numbers ?? [];
                  const expandable = isSerialized(item);
                  const open = expandedRow === index;
                  return (
                    <Fragment key={item.product_id ?? index}>
                      <TableRow
                        hover={expandable}
                        sx={{
                          "& > *": { borderBottom: "unset" },
                          cursor: expandable ? "pointer" : "default",
                        }}
                        onClick={() =>
                          expandable && setExpandedRow(open ? null : index)
                        }
                      >
                        <TableCell sx={{ width: 40 }}>
                          {expandable ? (
                            <IoChevronDown
                              style={{
                                transform: open ? "rotate(180deg)" : "none",
                                transition: "transform 150ms",
                              }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{productName(item)}</TableCell>
                        <TableCell>
                          {num(item.price).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {num(item.discount).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {num(item.net_price).toLocaleString()}
                        </TableCell>
                        <TableCell>{num(item.quantity)}</TableCell>
                        <TableCell>
                          {num(item.total_amount).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip title="تعديل المادة">
                              <IconButton
                                size="small"
                                onClick={() => editLine(index)}
                              >
                                <BiEdit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="حذف المادة">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                  remove(index);
                                  if (editIndex === index) cancelEdit();
                                  setExpandedRow(null);
                                }}
                              >
                                <BiTrash />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {expandable ? (
                        <TableRow>
                          <TableCell sx={{ py: 0 }} colSpan={9}>
                            <Collapse in={open} unmountOnExit>
                              <Box
                                sx={{ py: 1.5, maxHeight: 200, overflowY: "auto" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                  mb={1}
                                >
                                  {`الارقام التسلسلية ${serials.length}/${num(
                                    item.quantity
                                  )}`}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  sx={{ flexWrap: "wrap", rowGap: 1 }}
                                >
                                  {serials.map((serial) => (
                                    <Chip
                                      key={serial}
                                      size="small"
                                      label={serial}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              لم تتم اضافة اي مادة بعد
            </Typography>
          )}
        </PlainCard>
      </Stack>
    </FormDialog>
  );
};

export default InvoiceForm;
